import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGameState } from '../../hooks/useGameState';
import { apiService } from '../../services/api';
import NameEditForm from './components/NameEditForm/NameEditForm';
import RoleAssignment from './components/RoleAssignment/RoleAssignment';
import GuessingNumbers from './components/GuessingNumbers/GuessingNumbers';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { v4 as uuidv4 } from 'uuid';
import GamePageLayout from '../Layout/GamePageLayout';
import GameLobby from './components/GameLobby/GameLobby';
import SettingsModal from '../Common/Settings/SettingsModal';

const Game: React.FC = () => {
  const { t } = useTranslation();
  const { gameCode } = useParams<{ gameCode: string }>();
  const navigate = useNavigate();
  const [playerId] = useLocalStorage("playerId", uuidv4());
  const [playerName, setPlayerName] = useLocalStorage("playerName", "");
  
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [joined, setJoined] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  
  // Only start polling game state after player has joined
  const { gameState, loading, error, updateGameState } = useGameState(
    gameCode || null, 
    joined ? playerId : null
  );

  // Check if player is already in this game
  useEffect(() => {
    if (!gameCode) navigate("/")
    if (!playerName || playerName.length === 0) return

    setJoinError(null);
    setJoinLoading(true)

    console.log(`Joining ${gameCode}, with ${playerId}: ${playerName}`)
    apiService.joinGame(gameCode || "", playerId, playerName || "")
        .then(() => {
          setJoined(true)
        }).catch(error => {
          setJoinError(error.message)
        }).finally(() => {
          setJoinLoading(false)
        })
  }, [gameCode, playerId, playerName, navigate]);

  const pageTitle = useMemo(() => {
    if (!playerName || playerName.length === 0) {
      return t('game.pageTitle.whatsYourName');
    }

    switch (gameState?.state) {
      case 'lobby':
        return t('game.pageTitle.inviteOtherPlayers');
      case 'roleAssignment':
        return gameState?.isHost ? t('game.pageTitle.selectSituation') : t('game.pageTitle.waitForHost');
      case 'acting':
        return gameState?.isHost ? t('game.pageTitle.watchAndGuessNumbers') : t('game.pageTitle.actAccordingToNumber');
      case 'guessing':
      case 'results':
        return gameState?.isHost ? t('game.pageTitle.guessTheNumbers') : t('game.pageTitle.letsSeehowWellPlayed');
      default:
        return "";
    }
  }, [t, playerName, gameState?.state, gameState?.isHost])

  const handleStartGame = () => {
    if (!gameCode || !playerId) return;

    setJoinLoading(true)
    setJoinError(null)
    try {
      apiService.advanceGame(gameCode, playerId)
        .catch((error) => {
          setJoinError(error.message)
        })
        .finally(() => {
          setJoinLoading(false)
        });
    } catch (err) {
      setJoinError("failed to start the game")
    }
  };

  // Render game play states
  const renderGamePhase = () => {
    if (!playerName || playerName.length === 0) {
      return (
        <NameEditForm onSave={setPlayerName}/>
      );
    }

    if (!gameState) {
      return null;
    }

    switch (gameState.state) {
      case 'lobby':
        return <GameLobby gameState={gameState} onProceed={handleStartGame} isStarting={joinLoading} />;
      case 'roleAssignment':
      case 'acting':
        return <RoleAssignment gameState={gameState} playerId={playerId} onStateUpdate={updateGameState} />;
      case 'guessing':
      case 'results':
        return <GuessingNumbers gameState={gameState} playerId={playerId} onStateUpdate={updateGameState} />;
      default:
        return (
          <div className="card-container text-center flex-column gap-medium">
            <h2 className="section-title">Unknown game state: {gameState.state}</h2>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Back to Home
            </button>
          </div>
        );
    }
  };

  const handleOpenSettings = () => {
    setIsSettingsModalOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsModalOpen(false);
  };

  // Only show settings when player has joined and game state is available
  const showSettings = joined && !!gameState && !!playerName;

  return (
    <>
      <GamePageLayout
        title={pageTitle}
        error={error || joinError}
        loading={loading || joinLoading}
        showSettings={showSettings}
        onSettingsClick={handleOpenSettings}
      >
        {renderGamePhase()}
      </GamePageLayout>

      {gameState && (
        <SettingsModal
          isOpen={isSettingsModalOpen}
          onClose={handleCloseSettings}
          gameState={gameState}
          currentPlayerId={playerId}
          onGameStateUpdate={updateGameState}
        />
      )}
    </>
  );
};

export default Game;